import type { ReactNode } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import type { Permission } from '@quds/shared';
import { FORCED_PASSWORD_PATH, RequireAuth, RequirePermission } from '@/auth/RequireAuth';
import { AppShell } from '@/components/layout/AppShell';
import { ActivityLogPage } from '@/features/activity/ActivityLogPage';
import { ForcedPasswordChangePage } from '@/features/auth/ForcedPasswordChangePage';
import { LoginPage } from '@/features/auth/LoginPage';
import { CategoriesPage } from '@/features/categories/CategoriesPage';
import { CategoryComingSoonPage } from '@/features/categories/CategoryComingSoonPage';
import { OverviewPage } from '@/features/overview/OverviewPage';
import { PharmacyActionsProvider } from '@/features/pharmacies/actions/PharmacyActionsContext';
import { PharmacyDetailsPage } from '@/features/pharmacies/details/PharmacyDetailsPage';
import { PharmaciesPage } from '@/features/pharmacies/PharmaciesPage';
import { NotFoundPage } from '@/features/placeholder/NotFoundPage';
import { ProductionLinkingPage } from '@/features/production-linking/ProductionLinkingPage';
import { SettingsPage } from '@/features/settings/SettingsPage';
import { BusinessDetailsPage } from '@/features/supermarkets/BusinessDetailsPage';
import { SupermarketsPage } from '@/features/supermarkets/SupermarketsPage';
import { SyncStatusPage } from '@/features/sync/SyncStatusPage';
import { LicenseEnginePage } from '@/features/licenses/LicenseEnginePage';
import { DeploymentCenterPage } from '@/features/updates/DeploymentCenterPage';

/** Route-level permission gate (the API enforces the same permission). */
const gated = (permission: Permission, area: string, element: ReactNode) => (
  <RequirePermission permission={permission} area={area}>
    {element}
  </RequirePermission>
);

function PharmacyModule() {
  return (
    <PharmacyActionsProvider>
      <Outlet />
    </PharmacyActionsProvider>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<RequireAuth />}>
        <Route index element={<Navigate to="/categories" replace />} />
        <Route path={FORCED_PASSWORD_PATH} element={<ForcedPasswordChangePage />} />
        <Route path="/categories" element={<CategoriesPage />} />
        <Route path="/categories/:slug" element={<CategoryComingSoonPage />} />

        <Route element={<AppShell />}>
          <Route element={<PharmacyModule />}>
            <Route path="/overview" element={gated('pharmacy.read', 'the Overview', <OverviewPage />)} />
            <Route path="/pharmacies" element={gated('pharmacy.read', 'Pharmacies', <PharmaciesPage />)} />
            <Route path="/pharmacies/:id" element={gated('pharmacy.read', 'Pharmacies', <PharmacyDetailsPage />)} />
            <Route path="/supermarkets" element={gated('business.read', 'Supermarkets', <SupermarketsPage />)} />
            <Route
              path="/supermarkets/:id"
              element={gated('business.read', 'Supermarkets', <BusinessDetailsPage listPath="/supermarkets" listLabel="Supermarkets" />)}
            />
            <Route
              path="/production-linking"
              element={gated('production.read', 'Production Linking', <ProductionLinkingPage />)}
            />
            <Route path="/licenses" element={gated('license.read', 'License Engine', <LicenseEnginePage />)} />
            <Route path="/sync" element={gated('sync.read', 'Sync Status', <SyncStatusPage />)} />
            <Route path="/activity" element={gated('activity.read', 'the Activity Log', <ActivityLogPage />)} />
          </Route>
          <Route path="/updates" element={gated('update.read', 'Deployment Center', <DeploymentCenterPage />)} />
          <Route path="/settings" element={gated('settings.read', 'Settings', <SettingsPage />)} />
          {/* Devices / API & Integration are business-scoped only (Actions / Workspace). */}
          <Route path="/devices" element={<Navigate to="/categories" replace />} />
          <Route path="/api-integration" element={<Navigate to="/categories" replace />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Route>
    </Routes>
  );
}
